import { createSelector } from 'reselect';
import { defaultTo, prop } from 'ramda';
import ContactUsAPI from '@/api/contactUs';
import publicationAPI from '@/api/publications';
import eventRegistrationAPI from '@/api/eventRegister';
import eventAPI from '@/api/events';
import subscribeAPI from '@/api/subscribe'; // Import the subscribe API
import { APPLICATION_ACTION as ACTIONS } from '../actions';
import {
  contactUsPayloadProps,
  EventsPayloadProps,
  PublicationsPayloadProps,
  SubscribePayloadProps,
  EventRegisterPayloadProps,
} from '@/api/types';
import { Dispatch } from 'redux';

const initialState = {
  error: null,
  loading: false,
  contactResponse: '',
  additionalInformation: [],
  contactFormData: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: '',
    additionalInformation: [],
  },
  publications: [],
  events: [],
  subscriptionStatus: '', // New state for subscription response
  subLoading: false,
  eventRegistrationStatus: '',
  eventRegLoading: false,
};

// Avoid storing Axios/DOM Error objects in Redux — they mutate (e.g. event.currentTarget)
const serializeError = (error: unknown) => {
  if (!error) return null;
  if (typeof error === 'string') return error;

  const err = error as {
    message?: string;
    name?: string;
    code?: string;
    response?: { status?: number; data?: unknown };
  };

  return {
    message: err.message ?? 'Something went wrong',
    name: err.name,
    code: err.code,
    status: err.response?.status,
    data: err.response?.data,
  };
};

const getSlice = prop('app');

export const getError = createSelector(getSlice, prop('error'));

export const isLoading = createSelector(getSlice, prop('loading'));

export const subIsLoading = createSelector(getSlice, prop('subLoading'));

export const eventRegIsLoading = createSelector(getSlice, prop('eventRegLoading'));

export const getContactData = createSelector(getSlice, prop('contactResponse'));

export const getContactFormData = createSelector(getSlice, prop('contactFormData'));

export const getAdditionalInformation = createSelector(getSlice, prop('additionalInformation'));

export const getPublications = createSelector(getSlice, prop('publications'));

export const getEvents = createSelector(getSlice, prop('events'));

export const getSubscriptionStatus = createSelector(getSlice, prop('subscriptionStatus'));

export const getEventRegistrationStatus = createSelector(getSlice, prop('eventRegistrationStatus'));

export const submitContactForm = (payload: contactUsPayloadProps) => async (dispatch: Dispatch) => {
  dispatch({ type: ACTIONS.CONTACT_REQUEST });
  try {
    const data = await ContactUsAPI(payload);
    dispatch({
      type: ACTIONS.CONTACT_SUCCESS,
      contactData: data,
    });

    dispatch({
      type: ACTIONS.SET_ADDITIONAL_INFORMATION,
      value: payload.additionalInformation || [],
    });
  } catch (error) {
    dispatch({
      type: ACTIONS.CONTACT_FAILURE,
      error: error,
    });
  }
};

export const setContactFormData = (payload: contactUsPayloadProps) => (dispatch: Dispatch) => {
  const updatedFormData = { ...payload };

  dispatch({
    type: ACTIONS.SET_CONTACT_FORM_DATA,
    value: defaultTo('', updatedFormData),
  });
};

export const fetchPublications = (payload: PublicationsPayloadProps) => async (dispatch: Dispatch) => {
  dispatch({ type: ACTIONS.PUBLICTION_REQUEST });
  try {
    const data = await publicationAPI(payload);
    dispatch({
      type: ACTIONS.PUBLICTION_SUCCESS,
      publicationsData: data,
    });
  } catch (error) {
    dispatch({
      type: ACTIONS.PUBLICTION_FAILURE,
      error: error,
    });
  }
};

export const fetchEvents = (payload: EventsPayloadProps) => async (dispatch: Dispatch) => {
  dispatch({ type: ACTIONS.EVENT_REQUEST });
  try {
    const data = await eventAPI(payload);
    dispatch({
      type: ACTIONS.EVENT_SUCCESS,
      eventsData: data,
    });
  } catch (error) {
    dispatch({
      type: ACTIONS.EVENT_FAILURE,
      error: error,
    });
  }
};

export const subscribeToNotifications = (payload: SubscribePayloadProps) => async (dispatch: Dispatch) => {
  dispatch({ type: ACTIONS.SUBSCRIBE_REQUEST });
  try {
    const data = await subscribeAPI(payload);
    dispatch({
      type: ACTIONS.SUBSCRIBE_SUCCESS,
      subscriptionStatus: data, // Store subscription response
    });
  } catch (error) {
    dispatch({
      type: ACTIONS.SUBSCRIBE_FAILURE,
      error: error,
    });
  }
};

export const setAdditionalInformation = (value: []) => (dispatch: Dispatch) => {

  dispatch({
    type: ACTIONS.SET_ADDITIONAL_INFORMATION,
    value: defaultTo([], value),
  });
};

export const registerForEvent =
    (payload: EventRegisterPayloadProps) =>
    async (dispatch: Dispatch) => {
    dispatch({ type: ACTIONS.EVENTREG_REQUEST });

    try {
        const data = await eventRegistrationAPI(payload);
        const message = data?.message ?? 'Registration successful.';

        dispatch({
            type: ACTIONS.EVENTREG_SUCCESS
        });

        return { ok: true, message };
    } catch (err: any) {
        const status = err?.response?.status;
        const message =
            err?.response?.data?.message ??
            err?.message ??
            'Registration failed. Please try again.';

        if (status === 409) {
            dispatch({
                type: ACTIONS.EVENTREG_SUCCESS,
                eventRegistrationStatus: 'You are already registered for this event.',
            });
            return { ok: true, message };
        }

        dispatch({
            type: ACTIONS.EVENTREG_FAILURE,
            error: message,
        });

        return { ok: false, message };
    }
};

export default (
  state = initialState,
  action: {
    [x: string]: unknown;
    type?: string;
  }
) => {
  switch (action.type) {
    case ACTIONS.CONTACT_REQUEST:
      return {
        ...state,
        error: null,
        loading: true,
      };
    case ACTIONS.CONTACT_SUCCESS:
      return {
        ...state,
        error: null,
        loading: false,
        contactResponse: (action as { responseData: string }).responseData,
      };
    case ACTIONS.CONTACT_FAILURE:
      return {
        ...state,
        error: serializeError(action.error),
        loading: false,
      };
    case ACTIONS.SET_CONTACT_FORM_DATA: {
      return {
        ...state,
        contactFormData: action.value,
      };
    }
    case ACTIONS.SET_ADDITIONAL_INFORMATION:
      return {
        ...state,
        additionalInformation: action.value,
      };

    case ACTIONS.PUBLICTION_REQUEST:
    case ACTIONS.EVENT_REQUEST:
      return { ...state, loading: true, error: null };
    case ACTIONS.SUBSCRIBE_REQUEST:
      return { ...state, subLoading: true, error: null };

    case ACTIONS.PUBLICTION_SUCCESS:
      return {
        ...state,
        loading: false,
        publications: (action as { publicationsData: any }).publicationsData,
        error: null,
      };

    case ACTIONS.EVENT_SUCCESS:
      return {
        ...state,
        loading: false,
        events: (action as { eventsData: any }).eventsData,
        error: null,
      };

    case ACTIONS.SUBSCRIBE_SUCCESS:
      return {
        ...state,
        subLoading: false,
        subscriptionStatus: (action as { subscriptionStatus: string }).subscriptionStatus, // Store response
        error: null,
      };

    case ACTIONS.PUBLICTION_FAILURE:
    case ACTIONS.EVENT_FAILURE:
      return { ...state, loading: false, error: serializeError(action.error) };
    case ACTIONS.SUBSCRIBE_FAILURE:
      return { ...state, subLoading: false, error: serializeError(action.error) };

    case ACTIONS.EVENTREG_REQUEST:
      return { ...state, eventRegLoading: true, error: null, eventRegistrationStatus: ''  };
    case ACTIONS.EVENTREG_SUCCESS:
      return { ...state, eventRegLoading: false, 
        eventRegistrationStatus: (action as { eventRegistrationStatus: string })
          .eventRegistrationStatus, 
        error: null };
    case ACTIONS.EVENTREG_FAILURE: {
            const a = action as { error: string; status?: number };
            return {
                ...state,
                eventRegLoading: false,
                error: a.error,
                eventRegistrationStatus: a.status === 409 ? 'duplicate' : 'error',
            };
        }
    default:
      return state;
  }
};
